import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/es';

import TableFilters from '../components/TableFilters';
import { useTableDataSearch } from '../hooks/useSearchDataWithFilters';
import { getDeviceNamesByTables } from '../services/sensor.service';
import { EQUIPOS } from '../hooks/useDBTables';
import SensorChart from '../components/SensorChart';
import SensorStatisticsTable from '../components/SensorStadisticsTable';

// Extender dayjs con soporte para UTC

dayjs.extend(utc);

export default function ChartViewWithFilters() {
  const { search, searchStadistics } = useTableDataSearch();

  const [chartData, setChartData] = useState([]);
  const [stadisticsData, setStadisticsData] = useState([]);
  const [sensorNames, setSensorNames] = useState([]);
  const [visibleLines, setVisibleLines] = useState({});
  const [loading, setLoading] = useState(false);

  // Filtros
  const [startDate, setStartDate] = useState(dayjs().subtract(6, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const dataType = 'Temperaturas';
  const [smoothingType, setSmoothingType] = useState('Sin suavizar');
  const [prefix, setPrefix] = useState(EQUIPOS[0]);
  const [selectedSensor, setSelectedSensor] = useState('Todos');
  const [deviceOptions, setDeviceOptions] = useState([]);

  const [info, setInfo] = useState({
    type: 'info',
    message: 'Click en BUSCAR para traer datos',
    show: true,
  });

  const handleStartDateChange = (newValue) => setStartDate(newValue);
  const handleEndDateChange = (newValue) => setEndDate(newValue);
  const handlePrefixChange = (event) => {
    setPrefix(event.target.value);
    setSelectedSensor('Todos');
  };
  const handleSensorChange = (event) => {
    const value = event.target.value;
    setSelectedSensor(value);
    if (value !== 'Todos') {
      const [table] = value.split('-');
      setPrefix(table.trim());
    }
  };

  useEffect(() => {
    getDeviceNamesOptions();
  }, []);

  const getDeviceNamesOptions = async () => {
    const data = await getDeviceNamesByTables(EQUIPOS);
    setDeviceOptions(data);
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const result = await search({
        equipos: EQUIPOS,
        startDate,
        endDate,
        smoothingType,
        dataType,
        prefix,
        selectedSensor,
      });

      const stadistics = await searchStadistics({
        equipos: EQUIPOS,
        startDate,
        endDate,
        prefix,
        selectedSensor,
      });
      setStadisticsData(stadistics);
      if (!result || result.length === 0) {
        setInfo({
          type: 'warning',
          message: 'Sin datos disponibles',
          show: true,
        });
      } else {
        setInfo((prev) => ({ ...prev, show: false }));
      }

      const groupedByDate = {};

      result.forEach((reading) => {
        const time = dayjs.utc(reading.RecordTime).format('YYYY-MM-DD HH:mm');
        const sensorKey = `${reading.DeviceName} -  ${reading.SensorName}`;

        if (!groupedByDate[time]) {
          groupedByDate[time] = { date: time };
        }

        groupedByDate[time][sensorKey] = reading.SensorValue;
      });

      const transformed = Object.values(groupedByDate).sort((a, b) =>
        dayjs(a.date).isAfter(b.date) ? 1 : -1
      );
      setChartData(transformed);

      // Obtener nombres únicos de sensores
      const names = [
        ...new Set(
          result
            .filter((r) => r.DeviceName != null && r.SensorName != null)
            .map((r) => `${r.DeviceName} -  ${r.SensorName}`)
        ),
      ];

      setSensorNames(names);

      // Inicializar visibilidad de líneas
      const initialVisibility = {};
      names.forEach((name) => (initialVisibility[name] = true));
      setVisibleLines(initialVisibility);
    } catch (err) {
      setInfo({
        type: 'error',
        message: err.response.data.error || err.message,
        show: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Container sx={{ mt: 10 }}>
        <head>
          <title>Historial de Temperaturas con Filtros</title>
        </head>
        <Typography variant="h4" align="center" gutterBottom>
          Historial de Temperaturas
        </Typography>

        <TableFilters
          loading={loading}
          startDate={startDate}
          dataType={dataType}
          endDate={endDate}
          handleStartDateChange={handleStartDateChange}
          handleEndDateChange={handleEndDateChange}
          prefix={prefix}
          handlePrefixChange={handlePrefixChange}
          deviceOptions={deviceOptions}
          smoothingType={smoothingType}
          setSmoothingType={setSmoothingType}
          handleSearch={handleSearch}
          selectedSensor={selectedSensor}
          handleSensorChange={handleSensorChange}
        />

        {info.show && (
          <Alert
            severity={info.type}
            sx={{ mb: 2 }}
            onClose={() => setInfo((prev) => ({ ...prev, show: false }))}
          >
            {info.message}
          </Alert>
        )}

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="60vh"
          >
            <CircularProgress />
          </Box>
        ) : (
          chartData.length > 0 && (
            <>
              <SensorChart
                chartData={chartData}
                visibleLines={visibleLines}
                sensorNames={sensorNames}
                setVisibleLines={setVisibleLines}
              />
              <SensorStatisticsTable statistics={stadisticsData} />
            </>
          )
        )}
      </Container>
    </LocalizationProvider>
  );
}
